import {
	ChangeEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";

import { faXmark, IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import ReactDOM from "react-dom";

import { SearchField } from "@/components/SearchField/SearchField";
import { classNames } from "@/utils/classNames";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { Icon } from "../Icon/Icon";
import { Information } from "../Information/Information";

export interface AutoCompleteItem {
	id: number | string;
	title: string;
	icon?: JSX.Element;
	rightContent?: JSX.Element;
}

export interface AutoCompleteProps {
	handleSelect: (value?: AutoCompleteItem) => void;
	handleSearch?: (value: string) => void;
	handleReset?: () => void;
	selectedItem: string;
	selectedIcon?: JSX.Element;
	data: AutoCompleteItem[];
	label?: string;
	testId?: string;
	floating?: boolean;
	icon?: IconDefinition;
	position?: "top" | "bottom" | "left" | "right" | "bottom-right"; // | "left-center" ;
	className?: string;
	placeholder?: string;
	information?: string | JSX.Element;
	error?: boolean;
	isLoading?: boolean;
	isLoadingText?: string;
	disableLocalFilter?: boolean;
	/** Replaces the generic "no results" line, e.g. to say where the missing fields live. */
	noResultsText?: string;
	/**
	 * Replaces the list with this line, e.g. to say a search failed. Unlike `noResultsText` it shows
	 * even when `data` holds items, which still back a pick by typing an item's exact title.
	 */
	statusText?: string;
	/**
	 * Stacking of the portalled list, default `z-[10001]`. The list lands in `document.body`, so a
	 * trigger rendered inside a higher-stacked overlay needs to raise it or the list paints behind
	 * that overlay and reads as "no results" (the onboarding tour shell sits at `z-[99990]`).
	 */
	dropdownZIndexClassName?: string;
}

export const AutoComplete: FunctionComponent<AutoCompleteProps> = ({
	handleSelect,
	handleSearch,
	handleReset,
	selectedItem,
	selectedIcon,
	data,
	label,
	testId,
	position = "bottom",
	className,
	placeholder,
	information,
	error,
	isLoading,
	isLoadingText,
	disableLocalFilter,
	noResultsText,
	statusText,
	dropdownZIndexClassName = "z-[10001]",
}) => {
	const { t } = useTranslation();
	const [searchValue, setSearchValue] = useState("");

	// Dropdown Logic
	const dropdownRef = useRef<HTMLDivElement>(null);
	const [showDropdown, setShowDropdown] = useState(false);
	const selectFieldRef = useRef<HTMLDivElement>(null);

	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | null>(null);

	const updateDropdownPosition = useCallback(() => {
		if (selectFieldRef.current) {
			const rect = selectFieldRef.current.getBoundingClientRect();
			const viewportPadding = 16;
			const gap = 6;
			const desiredMaxHeight = 280;
			const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
			const spaceAbove = rect.top - viewportPadding;
			const shouldOpenAbove =
				(position === "bottom" || position === "bottom-right") &&
				spaceBelow < 180 &&
				spaceAbove > spaceBelow;
			const availableHeight = shouldOpenAbove ? spaceAbove : spaceBelow;
			const maxHeight = Math.max(140, Math.min(desiredMaxHeight, availableHeight - gap));
			const dropdownPositionStyle: React.CSSProperties = {
				maxHeight,
				position: "fixed",
				width: rect.width,
			};

			// Dynamically set dropdown position based on `position` prop
			switch (position) {
				case "top":
					dropdownPositionStyle.top = Math.max(viewportPadding, rect.top - maxHeight - gap);
					dropdownPositionStyle.left = rect.left;
					break;
				case "bottom":
					dropdownPositionStyle.top = shouldOpenAbove
						? Math.max(viewportPadding, rect.top - maxHeight - gap)
						: Math.min(window.innerHeight - maxHeight - viewportPadding, rect.bottom + gap);
					dropdownPositionStyle.left = rect.left;
					break;
				case "bottom-right":
					dropdownPositionStyle.top = shouldOpenAbove
						? Math.max(viewportPadding, rect.top - maxHeight - gap)
						: Math.min(window.innerHeight - maxHeight - viewportPadding, rect.bottom + gap);
					dropdownPositionStyle.left = Math.max(viewportPadding, rect.left - 120);
					break;
				case "left":
					dropdownPositionStyle.top = rect.top;
					dropdownPositionStyle.left = Math.max(viewportPadding, rect.left - rect.width - gap);
					break;
				case "right":
					dropdownPositionStyle.top = rect.top;
					dropdownPositionStyle.left = Math.min(
						window.innerWidth - rect.width - viewportPadding,
						rect.right + gap
					);
					break;
				default:
					dropdownPositionStyle.top = shouldOpenAbove
						? Math.max(viewportPadding, rect.top - maxHeight - gap)
						: Math.min(window.innerHeight - maxHeight - viewportPadding, rect.bottom + gap);
					dropdownPositionStyle.left = rect.left;
					break;
			}

			setDropdownStyle(dropdownPositionStyle);
		}
	}, [position]);

	useLayoutEffect(() => {
		if (!showDropdown) {
			setDropdownStyle(null);

			return;
		}

		updateDropdownPosition();
	}, [showDropdown, updateDropdownPosition, data, isLoading]);

	useEffect(() => {
		if (!showDropdown) {
			return;
		}

		window.addEventListener("scroll", updateDropdownPosition, true);
		window.addEventListener("resize", updateDropdownPosition);

		return () => {
			window.removeEventListener("scroll", updateDropdownPosition, true);
			window.removeEventListener("resize", updateDropdownPosition);
		};
	}, [showDropdown, updateDropdownPosition]);

	const handleSelectItem = (selected: AutoCompleteItem) => {
		handleSelect(selected);
		setShowDropdown(false);
	};

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		const selectedItem = data.find((item) => item.title === e.target.value);

		handleSearch && handleSearch(e.target.value);

		setSearchValue(e.target.value);

		if (selectedItem) {
			handleSelect(selectedItem);
		}

		setShowDropdown(true);
	};

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			const insideTrigger = selectFieldRef.current?.contains(target as Node);
			const insideDropdown = dropdownRef.current?.contains(target as Node);

			if (!insideTrigger && !insideDropdown) {
				if (showDropdown) {
					setShowDropdown(false);
				}
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [selectFieldRef, showDropdown]);

	useEffect(() => {
		setSearchValue(selectedItem);
	}, [selectedItem]);

	// Result rendering
	const renderResults = () => {
		const normalize = (input: string) => input.toLowerCase().replace(/\s+/g, "");
		const results = disableLocalFilter
			? data
			: data.filter((item) => normalize(item.title).includes(normalize(searchValue)));

		if (isLoading) {
			return (
				<p className="py-2.5 px-3 text-sm italic text-gray-700">{isLoadingText || "Loading..."}</p>
			);
		}

		if (statusText || !results.length) {
			return (
				<p className="py-2.5 px-3 text-sm italic text-gray-700">
					{statusText || noResultsText || t("basics.noResults")}
				</p>
			);
		}

		return (
			<div
				className={classNames(
					results.length ? "border-b border-b-border" : "",
					"flex flex-col items-start"
				)}
			>
				{results.map((item) => {
					const { id, title, icon, rightContent } = item;

					return (
						<button
							key={`option-select-${id}`}
							className={classNames(
								"flex py-2.5 px-3 pr-8 text-sm text-gray-900 duration-200 cursor-pointer relative items-center w-full text-left justify-between gap-3"
							)}
							onClick={(e) => {
								e.stopPropagation();
								handleSelectItem({ id, title });
							}}
						>
							<div className="flex items-center min-w-0">
								{icon && <div className="mr-2">{icon}</div>}
								<span className="truncate">{title}</span>
							</div>
							{rightContent && (
								<div className="flex items-center gap-2 shrink-0">{rightContent}</div>
							)}
						</button>
					);
				})}
			</div>
		);
	};

	const handleClickInput = () => setShowDropdown(true);

	return (
		<div ref={selectFieldRef} className="relative z-20 w-full">
			{label && (
				<label className="flex flex-row items-center block mb-2 text-sm font-medium text-gray-900 appearance-none">
					{label}
					{information && (
						<span className="ml-1">
							<Information information={information} />
						</span>
					)}
				</label>
			)}
			<div className="relative">
				{handleSearch && !selectedItem ? (
					<SearchField
						classNameInput={className}
						handleChange={handleSearchChange}
						handleReset={() => {
							setSearchValue("");
							handleSelect();
							handleReset && handleReset();
						}}
						isError={error}
						name="item"
						placeholder={placeholder || t("basics.pleaseSelect")}
						testId={testId}
						value={searchValue}
						onClick={handleClickInput}
					/>
				) : (
					""
				)}
				{selectedItem && (
					<div
						className="py-[9px] px-3 pr-3 cursor-pointer flex flex-row items-center justify-between rounded-xl text-ssm border border-border-light text-text bg-white relative items-center w-full"
						onClick={() => {
							setSearchValue("");
							handleSelect();
							handleReset && handleReset();
						}}
					>
						<span className="text-white bg-blue-action rounded-xl px-2 py-0.5 flex items-center gap-2">
							{selectedIcon && <span className="flex items-center">{selectedIcon}</span>}
							{selectedItem}
						</span>
						<Icon className="w-5 h-5 ml-1 cursor-pointer text-text" icon={faXmark} />
					</div>
				)}
			</div>
			{showDropdown &&
				ReactDOM.createPortal(
					<div
						ref={dropdownRef}
						className={classNames(
							"overflow-hidden bg-white border border-gray-200 rounded-xl overflow-y-auto bb-scrollbar-darker shadow-xl",
							dropdownZIndexClassName
						)}
						style={dropdownStyle ?? { visibility: "hidden", position: "fixed" }}
						onClick={(e) => e.stopPropagation()}
					>
						{renderResults()}
					</div>,
					document.body
				)}
		</div>
	);
};
